import { REFERRAL } from 'config/api-routes';
import { usePriceFormatter } from 'hooks/usePriceFormatter';
import useUser from 'hooks/useUser';
import { useEffect, useState } from 'react';
import { GET } from 'utils/request';
import { formatResponse } from 'utils/service-helpers';

type ReferralCodeData = {
  code: string;
  friendRewardAmount: number;
  friendValidityDays: number;
  inviterRewardAmount: number;
  inviterValidityDays: number;
  inviterRewardCap?: number | null;
  inviterRewardCapWindowDays?: number | null;
  inviterRewardCapReached?: boolean;
  minimumBasket: number;
  currency: string;
  adjustTrackerUrl: string | null;
  programState: 'live' | 'paused';
};

type ReferralCodeResult = { data: ReferralCodeData | null; error?: unknown };

const STALE_MS = 30 * 60 * 1000;

let cached: { userId: number; fetchedAt: number; promise: Promise<ReferralCodeResult>; result?: ReferralCodeResult } | null = null;

// Shared by the banner, the Refer a friend page and the FAQ, like the mobile react-query cache.
const loadReferralCode = (userId: number) => {
  if (cached?.userId === userId && Date.now() - cached.fetchedAt < STALE_MS) {
    return cached;
  }

  const entry: typeof cached = {
    userId,
    fetchedAt: Date.now(),
    promise: GET(REFERRAL.CODE.GET(), { silentFail: true })
      .then((response) => ({ data: formatResponse(response) }))
      .catch((error) => {
        if (cached === entry) {
          cached = null;
        }

        return { data: null, error };
      })
      .then((result) => {
        entry.result = result;

        return result;
      }),
  };

  cached = entry;

  return entry;
};

const peekReferralCode = (userId?: number) =>
  cached?.userId === userId && Date.now() - cached.fetchedAt < STALE_MS ? cached.result : undefined;

/** Referral code plus the live program values, money already formatted for copy. `null` data means the program is off. */
export const useReferralCode = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user } = useUser();
  const formatPrice = usePriceFormatter();
  const userId = user?.isLoggedIn ? user.id : undefined;
  const isActive = enabled && !!userId;
  const [result, setResult] = useState<ReferralCodeResult | undefined>(() => (isActive ? peekReferralCode(userId) : undefined));

  useEffect(() => {
    if (!isActive) {
      return;
    }

    let cancelled = false;

    setResult(peekReferralCode(userId));
    loadReferralCode(userId).promise.then((loaded) => {
      if (!cancelled) {
        setResult(loaded);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isActive, userId]);

  const data = result?.data;
  const formatAmount = (amount: number) => formatPrice(amount, { minimumFractionDigits: 0, currency: data?.currency });

  return {
    code: data?.code,
    friendReward: data ? formatAmount(data.friendRewardAmount) : undefined,
    inviterReward: data ? formatAmount(data.inviterRewardAmount) : undefined,
    minBasket: data ? formatAmount(data.minimumBasket) : undefined,
    friendValidityDays: data?.friendValidityDays,
    inviterValidityDays: data?.inviterValidityDays,
    inviterRewardCap: data?.inviterRewardCap,
    inviterRewardCapWindowDays: data?.inviterRewardCapWindowDays,
    inviterRewardCapReached: data?.inviterRewardCapReached ?? false,
    adjustTrackerUrl: data?.adjustTrackerUrl,
    isPaused: data?.programState === 'paused',
    isLoading: isActive && result === undefined,
    isError: isActive && result !== undefined && data === null,
    error: result?.error,
  };
};
